'use client';

import { useEffect, useRef, useState, ReactNode } from 'react';

interface ScrollRevealCardProps {
  children: ReactNode;
  className?: string;
  delay?: number;
}

// Helper to safely get/set scroll direction on window
function getScrollDirection(): 'down' | 'up' {
  if (typeof window === 'undefined') return 'down';
  if (!(window as any).__scrollDirection) {
    (window as any).__scrollDirection = 'down';
  }
  return (window as any).__scrollDirection;
}

function setScrollDirection(direction: 'down' | 'up'): void {
  if (typeof window !== 'undefined') {
    (window as any).__scrollDirection = direction;
  }
}

export default function ScrollRevealCard({ children, className = '', delay = 0 }: ScrollRevealCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasBeenVisibleRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScrollYRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      try {
        const currentScrollY = window.scrollY || window.pageYOffset;
        const lastScroll = lastScrollYRef.current;
        
        if (currentScrollY > lastScroll) {
          setScrollDirection('down');
        } else if (currentScrollY < lastScroll) {
          setScrollDirection('up');
        }
        
        lastScrollYRef.current = currentScrollY;
      } catch (error) {
        console.error('Scroll handler error:', error);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      try {
        window.removeEventListener('scroll', handleScroll);
      } catch (error) {
        // Ignore cleanup errors
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsVisible(true);
      return;
    }

    // Check if IntersectionObserver is available
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    let observer: IntersectionObserver | null = null;
    let mounted = true;

    try {
      observer = new IntersectionObserver(
        (entries) => {
          if (!mounted) return;

          entries.forEach((entry) => {
            try {
              if (entry.isIntersecting) {
                hasBeenVisibleRef.current = true;
                
                // Clear any existing timeout
                if (timeoutRef.current !== null) {
                  clearTimeout(timeoutRef.current);
                  timeoutRef.current = null;
                }
                
                // Show when intersecting
                timeoutRef.current = setTimeout(() => {
                  if (mounted) {
                    setIsVisible(true);
                  }
                }, delay);
              } else {
                // Hide when not intersecting and scrolling up (element is above viewport)
                const rect = entry.boundingClientRect;
                const isAboveViewport = rect.bottom < 0;
                const isBelowViewport = rect.top > (window.innerHeight || 0);
                
                if (hasBeenVisibleRef.current) {
                  const scrollDir = getScrollDirection();
                  
                  // If scrolling up and element is above viewport, hide it
                  if (scrollDir === 'up' && isAboveViewport) {
                    if (timeoutRef.current !== null) {
                      clearTimeout(timeoutRef.current);
                      timeoutRef.current = null;
                    }
                    if (mounted) {
                      setIsVisible(false);
                    }
                  }
                  // If scrolling down and element is below viewport, hide it (for reverse)
                  else if (scrollDir === 'down' && isBelowViewport) {
                    if (timeoutRef.current !== null) {
                      clearTimeout(timeoutRef.current);
                      timeoutRef.current = null;
                    }
                    if (mounted) {
                      setIsVisible(false);
                    }
                  }
                }
              }
            } catch (error) {
              console.error('IntersectionObserver callback error:', error);
            }
          });
        },
        { 
          threshold: 0.1,
          rootMargin: '0px 0px -100px 0px'
        }
      );

      const currentRef = cardRef.current;
      if (currentRef && observer) {
        observer.observe(currentRef);
      }
    } catch (error) {
      console.error('IntersectionObserver creation error:', error);
      setIsVisible(true);
    }

    return () => {
      mounted = false;
      
      try {
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        
        if (observer && cardRef.current) {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          observer.unobserve(cardRef.current);
        }
      } catch (error) {
        // Ignore cleanup errors
      }
    };
  }, [delay]);

  return (
    <div
      ref={cardRef}
      // Slides in, but is never hidden: content at opacity 0 until an observer
      // fires counted as late for LCP (the homepage team image), and stayed
      // invisible to anything that doesn't scroll. Reduced motion: no movement.
      className={`transition-transform duration-700 ease-out motion-reduce:transition-none motion-reduce:transform-none ${
        isVisible
          ? 'translate-y-0'
          : 'translate-y-8'
      } ${className}`}
    >
      {children}
    </div>
  );
}

